import { CommonModule } from "@angular/common";
import { Component, inject, Input } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { RestService } from "../../../core/services/rest.service";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";

interface ExecutionItem {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

@Component({
  selector: "app-start-execution-modal",
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatProgressSpinnerModule, DynamicFormComponent],
  templateUrl: "./start-execution-modal.component.html",
  styleUrl: "./start-execution-modal.component.scss",
})
export class StartExecutionModalComponent {
  activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  isActive: boolean = false;
  
  @Input() configs: any = {
    title: "",
    saveBtnText: "",
  };

  @Input() projectId: string = "";
  @Input() threadId: string = "";

  isSaving: boolean = false;

  executionItems: ExecutionItem[] = [
    {
      id: "1",
      title: "Link to release",
      description: "Link this execution to a release to enable end-to-end version tracking and actionable analytics",
      enabled: false,
    },
    {
      id: "2",
      title: "Test failure analysis",
      description: "Enable automated root cause analysis for test execution failures and automatically suggest possible resolutions",
      enabled: false,
    },
    {
      id: "3",
      title: "UI Self-healing",
      description: "Automatically resolve UI locator mismatches during test execution to prevent script failures",
      enabled: false,
    }
  ];

  // State A — existing releases dropdown
  releases: any[] = [];
  selectedReleaseId: string = '';
  isLoadingReleases: boolean = false;

  // State B — inline new release creation form
  showReleaseForm: boolean = false;
  isCreatingRelease: boolean = false;

  releaseForm: FormGroup = new FormGroup({
    releaseName:  new FormControl('', Validators.required),
    startDate:    new FormControl(null, Validators.required),
    endDate:      new FormControl(null, Validators.required),
    description:  new FormControl(''),
  });

  releaseFields: any[] = [
    {
      key: 'releaseName',
      name: 'Release name',
      type: 'text-input',
      placeholder: 'Enter release name',
      wrapperClass: 'col-md-12',
      validations: { required: true },
    },
    {
      key: 'startDate',
      name: 'Start Date',
      type: 'date-picker',
      wrapperClass: 'col-md-6',
      validations: { required: true },
    },
    {
      key: 'endDate',
      name: 'End Date',
      type: 'date-picker',
      wrapperClass: 'col-md-6',
      validations: { required: true },
    },
    {
      key: 'description',
      name: 'Release description',
      type: 'textarea',
      placeholder: 'Enter release description',
      wrapperClass: 'col-md-12',
    },
  ];

  getItemById(id: string): ExecutionItem | undefined {
    return this.executionItems.find((item) => item.id === id);
  }

  get hasAnyEnabled(): boolean {
    return this.executionItems.some(item => item.enabled);
  }

  private loadReleases(): void {
    this.isLoadingReleases = true;
    this.restService.getReleases(this.projectId).subscribe({
      next: (response: any) => {
        this.releases = response?.data ?? [];
        this.isLoadingReleases = false;
      },
      error: () => {
        this.releases = [];
        this.isLoadingReleases = false;
      },
    });
  }

  onToggleChange(item: ExecutionItem): void {
    if (item.id === '1') {
      if (item.enabled) {
        // Load existing releases and start at State A (dropdown)
        this.showReleaseForm = false;
        this.selectedReleaseId = '';
        this.loadReleases();
      } else {
        // Reset everything when toggle is switched OFF
        this.showReleaseForm = false;
        this.selectedReleaseId = '';
        this.releases = [];
        this._resetCreateForm();
      }
    }
  }

  /** Shows the inline release creation form (State B). */
  addNewRelease(): void {
    this._resetCreateForm();
    this.showReleaseForm = true;
  }

  /** ✓ Calls createRelease API, adds the result to the dropdown, and returns to State A. */
  confirmRelease(): void {
    if (this.releaseForm.invalid || this.isCreatingRelease) return;

    const v = this.releaseForm.value;
    const payload = {
      name: v.releaseName,
      startDate: this.toISODateTimeStr(v.startDate),
      releaseDate: this.toISODateTimeStr(v.endDate),
      description: v.description || '',
    };

    this.isCreatingRelease = true;
    this.restService.createRelease(this.projectId, payload).subscribe({
      next: (response: any) => {
        const newRelease = response?.data ?? { id: response?.id, name: payload.name };
        // Append to dropdown list and pre-select the new release
        this.releases = [...this.releases, newRelease];
        this.selectedReleaseId = newRelease.id;
        // Return to State A
        this.showReleaseForm = false;
        this._resetCreateForm();
        this.isCreatingRelease = false;
      },
      error: (err: any) => {
        console.error('Error creating release:', err);
        this.isCreatingRelease = false;
      },
    });
  }

  /** ✗ Returns to State A (dropdown) without disabling the toggle. */
  cancelRelease(): void {
    this.showReleaseForm = false;
    this._resetCreateForm();
  }

  /** Formats a Date to the required 'YYYY-MM-DDTHH:mm:ss.SSSSSS' (6 decimal) format. */
  private toISODateTimeStr(d: any): string {
    if (!d) return '';
    const iso = new Date(d).toISOString();      // e.g. "2026-03-19T19:24:45.440Z"
    const withoutZ = iso.slice(0, -1);           // "2026-03-19T19:24:45.440"
    const dotIdx = withoutZ.lastIndexOf('.');
    const ms = withoutZ.slice(dotIdx + 1);       // "440"
    return withoutZ.slice(0, dotIdx + 1) + ms.padEnd(6, '0'); // "2026-03-19T19:24:45.440000"
  }

  private _resetCreateForm(): void {
    this.releaseForm.reset();
  }

  isStartDisabled(): boolean {
    const item1 = this.getItemById('1');
    if (item1?.enabled) {
      if (this.showReleaseForm) {
        // Must fill in required fields for the new release
        return this.releaseForm.invalid;
      } else {
        // Must pick an existing release from the dropdown
        return !this.selectedReleaseId;
      }
    }
    return false;
  }

  start(): void {
    const item1 = this.getItemById('1');
    const item2 = this.getItemById('2');
    const item3 = this.getItemById('3');

    const enabledItems = this.executionItems.filter((item) => item.enabled);
    const responseData: any = { items: enabledItems, release: null };

    this.isSaving = true;

    /** Saves meta_data to the BE then closes the modal. */
    const saveMetaAndClose = (releaseId: string): void => {
      const payload = {
        project_id: this.projectId,
        thread_id: this.threadId,
        save_type: 'MetaData' as const,
        meta_data: {
          selfHealing: String(!!item3?.enabled),
          releaseId: releaseId,
          testFailureAnalysis: String(!!item2?.enabled),
        },
      };

      this.restService.saveExecutionMetaData(payload).subscribe({
        next: () => {
          this.isSaving = false;
          this.activeModal.close(responseData);
        },
        error: (error: any) => {
          console.error('Error saving execution meta data:', error);
          this.isSaving = false;
          // Proceed even on error so the user is not blocked
          this.activeModal.close(responseData);
        },
      });
    };

    if (item1?.enabled) {
      // Release already resolved via confirmRelease() — always use selectedReleaseId
      responseData.release = { id: this.selectedReleaseId };
      saveMetaAndClose(this.selectedReleaseId);
    } else {
      // No release linked — save meta_data with releaseId = 'false'
      saveMetaAndClose('false');
    }
  }
}
